// @ts-nocheck
import {
  CFG,
  DEFAULT_BINDS,
  ITEMS,
  ITEM_ORDER,
  KEYBINDS,
  KEY_TO_SLOT,
  REPORT_CATEGORIES,
  REPORT_CAT_ORDER,
  RESERVED_KEYS,
  ROBES,
  SLOT_ORDER,
  SPELLS,
  SPELLS_IN_SLOT,
  SPELL_AVAIL,
  SPELL_ORDER,
  SPELL_SCHOOLS,
  START_LOADOUT,
  UPGRADE,
  eventKeyLabel,
  isLocalDev,
  isTeammate,
  itemCost,
  keyLabelForSlot,
  rebuildKeyToSlot,
  saveKeybinds,
  spellAvail,
  spellInfo,
  spellSfx,
  spellShopDesc,
  spellShopPurchasable,
  teamColor,
} from "../../data";
import {
  TAU,
  bindTabPresenceUnload,
  clamp,
  len,
  lerp,
  rand,
  rgba,
  shade,
  shrinkState,
  tabPresenceId,
  track,
} from "../../util";
import {
  SPRITE_BRIM,
  armTimedProg,
  assetURL,
  bossImageByOct,
  drawArmChargeAccent,
  drawArmRangeTelegraph,
  drawEntityGroundShadow,
  drawSpellSprite,
  drawStar,
  fireballSpriteFXEnabled,
  healPodFromWire,
  makeWizardSprite,
  octIndex,
  pveBossName,
  refreshSpellSpriteDevFields,
  spellCastPopLayer,
  spellCastPopPose,
  spellSpriteAnimFrame,
  spellSpriteDevVisible,
  spellSpriteDrawSz,
  spellSpritePhaseDrawSz,
  spellSpritePhaseLife,
  spellSpritePhaseOpt,
  spellSpritePhaseReady,
  spellSpritePhaseSpec,
  spellSpriteSpec,
  updateSpellSpriteDevPanelVisibility,
  warlockImage,
  warlockImageByOct,
} from "../../sprites";
import {
  AUDIO_CREDITS,
  Sound,
} from "../../audio";
import {
  EMPTY_OPTS,
  Particle,
  Pillar,
  Player,
  Sigil,
  compact,
  recalcItems,
} from "../../entities";
import {
  Net,
  Tip,
  angLerp,
  angleDiff,
  escapeHtml,
  fmtWhen,
  modeLabel,
  ordinal,
  setupUiButtonSounds,
  showConfirm,
  spellLabel,
  spellTipHTML,
  tipAttr,
} from "../../net-ui";
import {
  lobbyInviteURL,
  parseLobbyInviteCode,
} from "../../lobby";

import { Game } from "../game-base";

Game.BZ_WIND_MAX = 22;
Game.BZ_SHARD_MAX = 30;
Game.BULLET_TIME_FX_SCALE = 0.5;

// Pre-baked soft sprites for the blizzard wind FX. The old path filled every
// wisp/ribbon with freshly built gradients under shadowBlur each frame — canvas
// shadowBlur is a per-fill Gaussian pass, and with several zones on screen it
// blew the whole frame budget on every client at once (zones are shared world
// state, so the hitch was synchronized across players). Baking the soft shapes
// once per zone color and stamping them with drawImage keeps the look without
// any per-frame gradient or blur work.
const bzSpriteCache = new Map();

function bzSoftSprite(kind, col) {
  const key = kind + ":" + col;
  let cv = bzSpriteCache.get(key);
  if (cv) return cv;
  const W = 128, H = 64;
  cv = document.createElement("canvas");
  cv.width = W;
  cv.height = H;
  const c = cv.getContext("2d");
  let g;
  if (kind === "puff") {
    // soft radial puff (old wisp core gradient, brightest just left of center)
    g = c.createRadialGradient(W * 0.46, H * 0.5, 0, W * 0.5, H * 0.5, W * 0.5);
    g.addColorStop(0, "rgba(245,253,255,0.5)");
    g.addColorStop(0.35, rgba(col, 0.28));
    g.addColorStop(0.72, rgba(col, 0.1));
    g.addColorStop(1, "rgba(0,0,0,0)");
  } else if (kind === "smear") {
    // horizontal motion-blur smear behind a wisp
    g = c.createLinearGradient(0, 0, W, 0);
    g.addColorStop(0, "rgba(0,0,0,0)");
    g.addColorStop(0.45, rgba(col, 0.12));
    g.addColorStop(0.85, "rgba(235,250,255,0.18)");
    g.addColorStop(1, "rgba(0,0,0,0)");
  } else {
    // "band": flowing gust ribbon
    g = c.createLinearGradient(0, 0, W, 0);
    g.addColorStop(0, "rgba(0,0,0,0)");
    g.addColorStop(0.22, rgba(col, 0.14));
    g.addColorStop(0.48, "rgba(228,252,255,0.22)");
    g.addColorStop(0.68, rgba(col, 0.12));
    g.addColorStop(1, "rgba(0,0,0,0)");
  }
  c.fillStyle = g;
  c.beginPath();
  c.ellipse(W / 2, H / 2, W / 2, H / 2, 0, 0, TAU);
  c.fill();
  bzSpriteCache.set(key, cv);
  return cv;
}

// Baked halo for falling meteor rocks (replaces per-frame radial gradients + shadowBlur).
function meteorGlowSprite(comet) {
  const key = "mg:" + (comet ? "ice" : "fire");
  let cv = bzSpriteCache.get(key);
  if (cv) return cv;
  const S = 64;
  cv = document.createElement("canvas");
  cv.width = cv.height = S;
  const c = cv.getContext("2d");
  const g = c.createRadialGradient(S * 0.5, S * 0.5, S * 0.1, S * 0.5, S * 0.5, S * 0.5);
  if (comet) {
    g.addColorStop(0, "rgba(126,200,255,0.55)");
    g.addColorStop(0.5, "rgba(126,200,255,0.18)");
    g.addColorStop(1, "rgba(126,200,255,0)");
  } else {
    g.addColorStop(0, "rgba(255,122,47,0.6)");
    g.addColorStop(0.5, "rgba(255,122,47,0.2)");
    g.addColorStop(1, "rgba(255,122,47,0)");
  }
  c.fillStyle = g;
  c.beginPath();
  c.arc(S * 0.5, S * 0.5, S * 0.5, 0, TAU);
  c.fill();
  bzSpriteCache.set(key, cv);
  return cv;
}

// Match server/sim bulletEntityDT: world fx slow unless owned by bullet-time caster.
Game.prototype.fxStepDT = function (dt, ownerId) {
  if (!this._bulletTimeActive) return dt;
  const btOwner = this._bulletTimeOwnerId;
  if (btOwner != null && ownerId != null && ownerId === btOwner) return dt;
  return dt * Game.BULLET_TIME_FX_SCALE;
};

Game.prototype.fxAnimScale = function (ownerId) {
  if (!this._bulletTimeActive) return 1;
  const btOwner = this._bulletTimeOwnerId;
  if (btOwner != null && ownerId != null && ownerId === btOwner) return 1;
  return Game.BULLET_TIME_FX_SCALE;
};

// Wire life ticks down each snapshot; a fresh cast resets to ~duration.
Game.prototype.blizzardFXContinues = function (old, wire) {
  if (!old || old.life == null || wire.l == null) return false;
  if (!!old.meteorShower !== !!wire.ms) return false;
  return wire.l <= old.life + 0.15;
}

Game.prototype.updateBlizzardWind = function (dt) {
    const active = this.state === "playing" || this.state === "countdown";
    for (const b of this.blizzards) {
      if (!b.windParts) b.windParts = [];
      const r = b.r || 175;
      const fade = b.life != null ? Math.min(1, b.life / 0.8) : 1;
      if (!active || fade <= 0) continue;
      const bdt = this.fxStepDT(dt, b.ownerId);
      const cap = this.blizzards.length > 2 ? Math.max(14, Game.BZ_WIND_MAX - 6) : Game.BZ_WIND_MAX;
      b._windAcc = (b._windAcc || 0) + bdt * 14 * fade;
      while (b._windAcc >= 1 && b.windParts.length < cap) {
        b._windAcc -= 1;
        const y = b.y + rand(-r * 0.78, r * 0.78);
        const x = b.x - r * rand(1.08, 1.28);
        b.windParts.push({
          x, y,
          vx: rand(220, 340) * rand(0.92, 1.08),
          vy: rand(-18, 18),
          life: rand(0.55, 1.05), maxLife: 1.05,
          w: rand(70, 130), h: rand(14, 26),
          phase: rand(0, TAU), flow: rand(1.6, 3.4), sway: rand(18, 38),
        });
      }
      compact(b.windParts, p => {
        p.life -= bdt;
        p.flowT = (p.flowT || 0) + bdt * p.flow;
        const gust = 0.82 + 0.18 * Math.sin(p.flowT * 2.1 + p.phase);
        p.x += p.vx * gust * bdt;
        p.y += (p.vy + Math.sin(p.phase + p.x * 0.007 + p.flowT * 3.2) * p.sway) * bdt;
        const dx = p.x - b.x, dy = p.y - b.y;
        return p.life > 0 && dx < r * 1.48 && dx > -r * 1.34 && Math.abs(dy) < r * 1.12;
      });
    }
  }

Game.prototype.blizzardWindOverFade = function (dist, r) {
    if (dist <= r) return 1;
    return clamp(1 - (dist - r) / (r * 0.38), 0, 1);
  }

Game.prototype.drawBlizzardWindWisp = function (c, px, py, p, r, col, fade, lite) {
    const rim = this.blizzardWindOverFade(Math.hypot(px, py), r);
    const born = clamp(1 - p.life / (p.maxLife || 1), 0, 1);
    const a = clamp(p.life / (p.maxLife || 1), 0, 1) * fade * rim;
    const inOut = Math.sin(born * Math.PI);
    const flowY = Math.sin(p.phase + px * 0.011 + (p.flowT || 0) * 3.1) * p.sway * 0.35;
    const y = py + flowY;
    const w = p.w * (0.9 + 0.12 * Math.sin((p.flowT || 0) * 4 + p.phase));
    const h = p.h * (0.95 + 0.08 * Math.cos(p.phase * 1.7));
    const trail = w * (1.15 + 0.35 * inOut);

    c.save();
    c.translate(px, y);
    c.globalCompositeOperation = "lighter";

    if (!lite) {
      // trailing smear (motion blur along wind direction) — baked sprite stamp
      c.globalAlpha = a * 0.22 * inOut;
      c.drawImage(bzSoftSprite("smear", col),
        -trail * 0.93, -h * 0.38, trail * 1.1, h * 0.76);
    }

    // main soft puff — drawn ~1.25x the old ellipse so the baked falloff also
    // covers the glow the removed shadowBlur used to add
    c.globalAlpha = a * 0.38 * inOut;
    c.drawImage(bzSoftSprite("puff", col),
      -w * 0.9, -h * 0.425, w * 1.8, h * 0.85);

    c.restore();
  }

Game.prototype.drawBlizzardIcyWind = function (c, r, col, fade, pulse, t, b) {
    c.save();
    c.globalCompositeOperation = "lighter";
    const anim = this.fxAnimScale(b.ownerId);

    // flowing gust ribbons — wide, soft, vertically rippling (baked sprite stamps)
    const heavy = this.blizzards.length > 2;
    const sheetN = heavy ? 3 : 5;
    const band = bzSoftSprite("band", col);
    for (let i = 0; i < sheetN; i++) {
      const yOff = sheetN > 1 ? ((i / (sheetN - 1)) - 0.5) * r * 1.3 : 0;
      const span = r * 4.2;
      const spd = (72 + i * 11) * anim;
      const scroll = ((t * spd + i * 73 + (b.pulse || 0) * 6) % span) - span * 0.48;
      const ripple = Math.sin(t * 1.6 * anim + i * 1.1) * 10 + Math.sin(t * 3.4 * anim + i * 2.3) * 4;
      const rim = this.blizzardWindOverFade(Math.abs(yOff), r);
      const al = fade * rim * (0.05 + 0.06 * pulse) * (0.55 + 0.45 * Math.sin(t * 3.2 + i * 0.85));
      const bandH = 22 + i * 2;
      const bandW = r * 1.55;

      c.save();
      c.translate(scroll, yOff + ripple);
      c.globalAlpha = al;
      // ~1.2x the old ellipse so the baked falloff also covers the removed blur halo
      c.drawImage(band, -bandW * 0.6, -bandH * 0.46, bandW * 1.2, bandH * 0.92);
      c.restore();
    }

    const wispLite = heavy;
    for (const p of (b.windParts || [])) {
      this.drawBlizzardWindWisp(c, p.x - b.x, p.y - b.y, p, r, col, fade, wispLite);
    }
    c.restore();
    c.globalCompositeOperation = "source-over";
    c.globalAlpha = 1;
  }

Game.prototype.drawBlizzardCircle = function (c, r, col, fade, pulse, t) {
    c.save();
    c.fillStyle = rgba(col, (0.05 + 0.09 * pulse) * fade);
    c.beginPath(); c.arc(0, 0, r, 0, TAU); c.fill();
    c.globalCompositeOperation = "lighter";
    c.strokeStyle = rgba(col, (0.28 + 0.22 * pulse) * fade);
    c.lineWidth = 2.5;
    c.setLineDash([10, 14]); c.lineDashOffset = -t * 24;
    c.beginPath(); c.arc(0, 0, r, 0, TAU); c.stroke();
    c.setLineDash([]);
    c.restore();
  }

Game.prototype.updateBlizzardShards = function (dt) {
    const active = this.state === "playing" || this.state === "countdown";
    const windX = -0.74, windY = 0.67;
    const perpX = -windY, perpY = windX;
    for (const b of this.blizzards) {
      if (!b.shards) b.shards = [];
      const r = b.r || 175;
      const fade = b.life != null ? Math.min(1, b.life / 0.8) : 1;
      if (!active || fade <= 0) continue;
      const bdt = this.fxStepDT(dt, b.ownerId);
      const cap = this.blizzards.length > 2 ? Math.max(18, Game.BZ_SHARD_MAX - 8) : Game.BZ_SHARD_MAX;
      b._spawnAcc = (b._spawnAcc || 0) + bdt * 54 * fade;
      while (b._spawnAcc >= 1 && b.shards.length < cap) {
        b._spawnAcc -= 1;
        const a = rand(0, TAU);
        const dist = Math.sqrt(Math.random()) * r;
        const tx = b.x + Math.cos(a) * dist;
        const ty = b.y + Math.sin(a) * dist;
        let backDist = rand(r * 1.15, r * 1.55);
        const jitter = rand(-r * 0.04, r * 0.04);
        let sx = tx - windX * backDist + perpX * jitter;
        let sy = ty - windY * backDist + perpY * jitter;
        if (Math.hypot(sx - b.x, sy - b.y) < r * 1.05) {
          backDist = r * 1.45;
          sx = tx - windX * backDist + perpX * jitter;
          sy = ty - windY * backDist + perpY * jitter;
        }
        const speed = rand(470, 550);
        const travel = backDist / speed;
        const meteorShower = !!b.meteorShower;
        b.shards.push({
          x: sx, y: sy, tx, ty,
          vx: windX * speed, vy: windY * speed,
          totalDist: backDist,
          life: travel + rand(0.04, 0.1),
          maxLife: travel + 0.1,
          size: meteorShower ? rand(8, 12) : rand(9, 13),
          fire: meteorShower,
        });
      }
      compact(b.shards, sh => {
        sh.life -= bdt;
        sh.x += sh.vx * bdt;
        sh.y += sh.vy * bdt;
        const remain = Math.hypot(sh.x - sh.tx, sh.y - sh.ty);
        return sh.life > 0 && remain > 6;
      });
    }
  }

Game.prototype.drawFallingIceShard = function (c, x, y, sh, col, alpha) {
    const ang = Math.atan2(sh.vy, sh.vx);
    const remain = Math.hypot(x - sh.tx, y - sh.ty);
    const prog = 1 - clamp(remain / (sh.totalDist || 1), 0, 1);
    const scale = 1 - prog * 0.58;
    const size = sh.size * scale;
    const fade = alpha * (0.75 + 0.25 * (1 - prog));
    c.save();
    c.translate(x, y);
    c.rotate(ang);
    const L = size * 2.0;
    const W = size * 0.28;
    const trail = size * 1.1;

    c.globalCompositeOperation = "lighter";
    c.globalAlpha = fade * 0.22;
    c.strokeStyle = col; c.lineWidth = Math.max(0.8, size * 0.14); c.lineCap = "round";
    c.beginPath(); c.moveTo(-trail, 0); c.lineTo(L * 0.2, 0); c.stroke();

    c.globalAlpha = fade;
    c.fillStyle = "rgba(228,248,255,0.96)";
    c.strokeStyle = "rgba(170,220,255,0.85)"; c.lineWidth = 1;
    c.beginPath();
    c.moveTo(L, 0);
    c.lineTo(0, -W); c.lineTo(-L * 0.18, 0);
    c.lineTo(0, W); c.closePath();
    c.fill(); c.stroke();
    c.restore();
  }

Game.prototype.blizzardZoneColor = function (b) {
    if (b?.meteorShower) return (SPELLS.meteor || {}).color || "#ff6a3a";
    return (SPELLS.blizzard || {}).color || "#7ec8ff";
  }

Game.prototype.drawFallingFireballShard = function (c, x, y, sh, col, alpha) {
    const ang = Math.atan2(sh.vy, sh.vx);
    const remain = Math.hypot(x - sh.tx, y - sh.ty);
    const prog = 1 - clamp(remain / (sh.totalDist || 1), 0, 1);
    const scale = 1 - prog * 0.58;
    const r = sh.size * 0.34 * scale;
    const fade = alpha * (0.75 + 0.25 * (1 - prog));
    c.save();
    c.translate(x, y);
    c.globalAlpha = fade;
    this.glowOrb(c, r, col, ang, "#fff0d0");
    c.restore();
  }

Game.prototype.drawBlizzards = function () {
    const c = this.ctx;
    const t = this.nowS;
    for (const b of this.blizzards) {
      const col = this.blizzardZoneColor(b);
      const r = b.r || 175;
      const fade = b.life != null ? Math.min(1, b.life / 0.8) : 1;
      const pulse = 0.5 + 0.5 * Math.sin(t * 7 + (b.pulse || 0));
      c.save();
      c.translate(b.x, b.y);
      this.drawBlizzardCircle(c, r, col, fade, pulse, t);
      this.drawBlizzardIcyWind(c, r, col, fade, pulse, t, b);
      c.restore();
    }
  }

Game.prototype.drawBlizzardRain = function () {
    const c = this.ctx;
    for (const b of this.blizzards) {
      const col = this.blizzardZoneColor(b);
      const fade = b.life != null ? Math.min(1, b.life / 0.8) : 1;
      for (const sh of (b.shards || [])) {
        const a = clamp(sh.life / (sh.maxLife || 0.95), 0.35, 1) * fade;
        if (b.meteorShower || sh.fire) this.drawFallingFireballShard(c, sh.x, sh.y, sh, col, a);
        else this.drawFallingIceShard(c, sh.x, sh.y, sh, col, a);
      }
    }
    c.globalAlpha = 1;
  }

const METEOR_APPROACH_DEG = 70;
const METEOR_STREAK_START = 0.58; // longer visible slam — telegraph for the first ~58%
const METEOR_STREAK_EASE = 1.75;   // gentler ease-in: crosses the screen, then accelerates into impact

/** Screen-space streak: raw 0..1 time in the slam window, streak 0..1 position along the path. */
function meteorStreakPhase(p) {
  if (p <= METEOR_STREAK_START) return null;
  const raw = (p - METEOR_STREAK_START) / (1 - METEOR_STREAK_START);
  const streak = 1 - Math.pow(1 - raw, METEOR_STREAK_EASE);
  return { raw, streak };
}

function meteorStreakDist() {
  // Well off-screen from the impact point so the rock visibly crosses the arena.
  return (CFG.viewRadius || 640) * 2.45;
}

Game.prototype.drawMeteorRock = function (c, rad, comet, travelAng, streak, pr, mx, my) {
  const iceCore = "#aef0ff";
  const iceHot = "#d4f4ff";
  const tailLen = 120 + 340 * streak;
  c.save();
  c.translate(mx, my);
  c.rotate(travelAng);
  c.globalAlpha *= 0.82 + 0.18 * streak;

  if (streak > 0.06) {
    c.save();
    c.globalCompositeOperation = "lighter";
    c.strokeStyle = comet ? "rgba(160,230,255,0.55)" : "rgba(255,170,90,0.55)";
    c.lineWidth = 6 + 18 * streak;
    c.lineCap = "round";
    c.beginPath();
    c.moveTo(tailLen * 0.15, 0);
    c.lineTo(-tailLen, 0);
    c.stroke();
    c.restore();
  }

  if (comet) {
    c.save();
    c.globalCompositeOperation = "lighter";
    c.fillStyle = "rgba(120,210,255,0.42)";
    c.beginPath(); c.moveTo(-rad * 0.7, -rad * 0.35); c.lineTo(rad * 0.7, -rad * 0.35); c.lineTo(-tailLen, 0); c.closePath(); c.fill();
    c.fillStyle = "rgba(220,245,255,0.45)";
    c.beginPath(); c.moveTo(-rad * 0.35, -rad * 0.35); c.lineTo(rad * 0.35, -rad * 0.35); c.lineTo(-tailLen * 0.65, 0); c.closePath(); c.fill();
    c.restore();
    if (this.state === "playing" && Math.random() < 0.55 + streak * 0.45)
      this.particles.push(new Particle(mx, my, Math.cos(travelAng) * rand(80, 220), Math.sin(travelAng) * rand(80, 220),
        rand(0.22, 0.45), Math.random() < 0.5 ? iceHot : iceCore, rand(3, 7), { kind: "glow", grav: -20 }));
    c.save();
    c.globalCompositeOperation = "lighter";
    c.globalAlpha *= 0.35 + 0.25 * streak;
    const iceGlowR = rad * (1.6 + streak * 0.8);
    c.drawImage(meteorGlowSprite(true), -iceGlowR, -iceGlowR, iceGlowR * 2, iceGlowR * 2);
    c.restore();
    c.globalCompositeOperation = "source-over";
    c.fillStyle = "#1a3048";
    c.beginPath(); c.arc(0, 0, rad, 0, TAU); c.fill();
    c.fillStyle = "#6ec8e8";
    c.beginPath(); c.arc(-rad * 0.25, -rad * 0.2, rad * 0.55, 0, TAU); c.fill();
    c.fillStyle = iceHot;
    c.beginPath(); c.arc(-rad * 0.3, -rad * 0.3, rad * 0.25, 0, TAU); c.fill();
  } else {
    c.save();
    c.globalCompositeOperation = "lighter";
    c.fillStyle = "rgba(255,150,70,0.4)";
    c.beginPath(); c.moveTo(-rad * 0.7, -rad * 0.35); c.lineTo(rad * 0.7, -rad * 0.35); c.lineTo(-tailLen, 0); c.closePath(); c.fill();
    c.fillStyle = "rgba(255,210,120,0.4)";
    c.beginPath(); c.moveTo(-rad * 0.35, -rad * 0.35); c.lineTo(rad * 0.35, -rad * 0.35); c.lineTo(-tailLen * 0.65, 0); c.closePath(); c.fill();
    c.restore();
    if (this.state === "playing" && Math.random() < 0.6 + streak * 0.4)
      this.particles.push(new Particle(mx, my, Math.cos(travelAng) * rand(90, 260), Math.sin(travelAng) * rand(90, 260),
        rand(0.22, 0.5), Math.random() < 0.5 ? "#ffd479" : "#ff7a2f", rand(3, 7), { kind: "glow", grav: -30 }));
    c.save();
    c.globalCompositeOperation = "lighter";
    c.globalAlpha *= 0.38 + 0.28 * streak;
    const fireGlowR = rad * (1.7 + streak * 0.9);
    c.drawImage(meteorGlowSprite(false), -fireGlowR, -fireGlowR, fireGlowR * 2, fireGlowR * 2);
    c.restore();
    c.globalCompositeOperation = "source-over";
    c.fillStyle = "#3a2418";
    c.beginPath(); c.arc(0, 0, rad, 0, TAU); c.fill();
    c.fillStyle = "#ff8a3d";
    c.beginPath(); c.arc(-rad * 0.25, -rad * 0.2, rad * 0.55, 0, TAU); c.fill();
    c.fillStyle = "#ffd479";
    c.beginPath(); c.arc(-rad * 0.3, -rad * 0.3, rad * 0.25, 0, TAU); c.fill();
  }
  c.restore();
};

Game.prototype.meteorTelegraphVisible = function (ownerId) {
    if (ownerId == null) return false;
    const viewer = this.voidDangerSubject?.() || this.human();
    if (!viewer) return false;
    if (ownerId == viewer.id) return true;
    const owner = this.playerById?.[ownerId] ?? this.playerById?.[String(ownerId)];
    if (owner && owner.boss) return true;
    return !!(owner && isTeammate(viewer, owner.team));
  }

Game.prototype.drawMeteor = function (pr, s) {
    const c = this.ctx;
    const t = this.nowS;
    // fall progress 0~1 (online comes from the wire, offline from age/life)
    const p = typeof pr.prog === "number"
      ? clamp(pr.prog, 0, 1)
      : clamp((pr.t || 0) / (s.life || 1), 0, 1);
    const comet = !!pr.comet;
    const nuke = !!pr.nuke && !comet;
    const blast = (s.detRadius || 200) * (nuke ? 2 : 1);
    const showTelegraph = this.meteorTelegraphVisible(pr.ownerId);

    // ---- ground telegraph at the impact point (caster + allies only) ----
    if (showTelegraph) {
      const iceWarn = comet ? `rgba(120,210,255,${0.06 + 0.16 * p})` : `rgba(255,90,40,${0.06 + 0.16 * p})`;
      const iceRim = comet ? `rgba(160,230,255,${0.12 + 0.5 * p})` : `rgba(255,140,70,${0.12 + 0.5 * p})`;
      const iceReticle = comet ? "rgba(200,240,255,0.9)" : "rgba(255,200,120,0.9)";
      c.save();
      c.translate(pr.x, pr.y);
      // danger zone: a filled disc + animated dashed rim that brightens on approach
      c.fillStyle = iceWarn;
      c.beginPath(); c.arc(0, 0, blast, 0, TAU); c.fill();
      c.save();
      c.globalCompositeOperation = "lighter";
      c.strokeStyle = iceRim;
      c.lineWidth = 3 + 3 * p;
      c.setLineDash([14, 10]); c.lineDashOffset = -t * 40;
      c.beginPath(); c.arc(0, 0, blast, 0, TAU); c.stroke();
      // a contracting reticle ring that converges on the center as it lands
      c.setLineDash([]);
      c.globalAlpha = 0.5 + 0.5 * p;
      c.strokeStyle = iceReticle; c.lineWidth = 2;
      c.beginPath(); c.arc(0, 0, blast * (1 - p) + 14, 0, TAU); c.stroke();
      // crosshair ticks
      c.beginPath();
      for (let i = 0; i < 4; i++) {
        const a = i * TAU / 4 + Math.PI / 4;
        c.moveTo(Math.cos(a) * (blast - 16), Math.sin(a) * (blast - 16));
        c.lineTo(Math.cos(a) * blast, Math.sin(a) * blast);
      }
      c.stroke();
      c.restore();
      // impact shadow that grows as the rock descends
      const sh = 0.4 + 0.6 * p;
      c.fillStyle = comet ? `rgba(40,70,110,${0.22 + 0.18 * p})` : `rgba(0,0,0,${0.25 + 0.25 * p})`;
      c.beginPath(); c.ellipse(0, 0, pr.radius * 1.6 * sh, pr.radius * 0.8 * sh, 0, 0, TAU); c.fill();
      c.restore();
    }

    // ---- streak in from top-right at the last moment ----
    const phase = meteorStreakPhase(p);
    if (phase) {
      const { streak } = phase;
      const ang = METEOR_APPROACH_DEG * Math.PI / 180;
      const dist = meteorStreakDist();
      const remain = 1 - streak;
      const mx = pr.x + Math.cos(ang) * dist * remain;
      const my = pr.y - Math.sin(ang) * dist * remain;
      const travelAng = Math.atan2(Math.sin(ang), -Math.cos(ang));
      const rad = pr.radius * (0.9 + 0.55 * streak);
      this.drawMeteorRock(c, rad, comet, travelAng, streak, pr, mx, my);
    }
  }
